import { Button, InputNumber } from 'antd';
import { BaseSyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QUANTITY } from '@common/constants';
import formatUOM from '@common/helpers/formatUOM';
import { handleDecimalNumberInput } from '@common/utils';

import { QUANTITY_SELECTOR_ERRORS } from './QuantitySelector.constants';
import * as QuantitySelectorUtils from './QuantitySelector.utils';

import './QuantitySelector.scss';

export interface QuantitySelectorProps {
    value: number;
    unitOfMeasure?: string;
    min?: number;
    minErrorMessage?: string;
    max?: number;
    maxErrorMessage?: string;
    onValueChange?: (value: number) => void;
}

/**
 * Quantity selector component that notifies a value change whe a button is
 * clicked, enter is pressed or input looses focus. Does NOT notifies changes
 * while user is typing.
 */
const QuantitySelector = ({
    value,
    unitOfMeasure,
    min,
    minErrorMessage,
    max,
    maxErrorMessage,
    onValueChange = () => undefined,
}: QuantitySelectorProps): JSX.Element => {
    const { t } = useTranslation();
    const validator = QuantitySelectorUtils.getValidator(min, max);
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        if (localValue !== value) {
            setLocalValue(value);
        }
    }, [value]);

    const handleError = (errorCode: QUANTITY_SELECTOR_ERRORS) => {
        let errorMessage = t('QUANTITY_SELECTOR.DEFAULT_ERROR_MESSAGE');
        switch (errorCode) {
            case QUANTITY_SELECTOR_ERRORS.MAX:
                if (maxErrorMessage) {
                    errorMessage = maxErrorMessage;
                }
                break;
            case QUANTITY_SELECTOR_ERRORS.MIN:
                if (minErrorMessage) {
                    errorMessage = minErrorMessage;
                }
                break;
            case QUANTITY_SELECTOR_ERRORS.INVALID:
            default:
                break;
        }
        QuantitySelectorUtils.showError(t('QUANTITY_SELECTOR.DEFAULT_ERROR_TITLE'), errorMessage);
    };

    const handleInputExit = (e: BaseSyntheticEvent) => {
        const newValue = Number(e.target.value);
        const { errorCode } = validator(newValue);
        if (errorCode) {
            handleError(errorCode);
        } else {
            onValueChange(newValue);
        }
    };

    const handleButtonClick = (newValue: number) => {
        if (newValue < (min ?? 0) || newValue > (max ?? Number.MAX_SAFE_INTEGER)) {
            return;
        }
        setLocalValue(newValue);
        onValueChange(newValue);
    };

    return (
        <div onClick={(e) => e.stopPropagation()} className="QuantitySelector">
            <Button
                type="text"
                className="QuantitySelector__button sub-action"
                style={{ color: localValue === min ?? 0 ? 'transparent' : 'inherit' }}
                onClick={() => handleButtonClick(localValue - 1)}
                disabled={localValue === min ?? 0}
            >
                {QUANTITY.MINUS}
            </Button>
            <InputNumber
                className="QuantitySelector__input"
                value={localValue}
                min={min}
                max={max}
                onKeyDown={handleDecimalNumberInput}
                onChange={(newValue) => setLocalValue(newValue ?? 0)}
                onPressEnter={handleInputExit}
                onBlur={handleInputExit}
            />
            {unitOfMeasure && <span>{formatUOM(unitOfMeasure)}</span>}
            <Button
                type="text"
                className="QuantitySelector__button sub-action"
                onClick={() => handleButtonClick(localValue + 1)}
                disabled={localValue === max ?? Number.MAX_SAFE_INTEGER}
            >
                {QUANTITY.PLUS}
            </Button>
        </div>
    );
};

export default QuantitySelector;
